import {useMemo} from 'react';
import {BrowseCourse} from '@udemy-v2/browse-course';
import {TrackableDiscoveryItem} from '@udemy-v2/react-browse-event-tracking';
import {
    RecommendationItem,
    RecommendationUnit,
    useRecommendationsSections,
} from '@udemy-v2/react-recommendations';
import {ResponsiveBrowseCourseCarousel} from '@/features/course-landing-page/components/browse-course-carousel/responsive-browse-course-carousel.react-component';
import styles from './limited-access-recommendations-carousel.module.scss';
import {CAROUSEL_CONFIGURATIONS} from "@udemy-v2/react-browse-course";

const sectionToUnits = (
    section: ReturnType<typeof useRecommendationsSections>['data'][number],
): RecommendationUnit[] => {
    if ('units' in section) return section.units;
    if ('items' in section) return [section];
    return [];
};

const unitToCourses = (unit: RecommendationUnit): TrackableDiscoveryItem<BrowseCourse>[] => {
    const courses: TrackableDiscoveryItem<BrowseCourse>[] = [];
    unit.items.forEach((item: RecommendationItem) => {
        if (item && typeof item === 'object' && 'course' in item && item.course) {
            courses.push({
                ...(item.course as BrowseCourse),
                trackingId: item.trackingId,
                frontendTrackingId: item.frontendTrackingId,
            });
        }
    });
    return courses;
};

/**
 * Reads recommendation sections fro
 * m the surrounding RecommendationsProvider
 * (see [[LimitedAccessRecommendationsWrapper]]) and renders one
 * `ResponsiveBrowseCourseCarousel` per returned recommendation unit. Renders nothing
 * while loading or when no unit returned a course list.
 */
export const LimitedAccessRecommendationsCarousel = () => {
    const {data: sections, status} = useRecommendationsSections();

    const carousels = useMemo(() => {
        if (status === 'loading' || sections.length === 0) return [];
        return sections
            .flatMap(sectionToUnits)
            .map((unit) => ({
                id: unit.id,
                title: unit.title,
                courses: unitToCourses(unit),
            }))
            .filter((unit) => unit.courses.length > 0);
    }, [sections, status]);

    if (status === 'loading' || carousels.length === 0) return null;

    return (
        <div className={styles['container']}>
            {carousels.map(({id, title, courses}) => (
                <section key={id} className={styles['unit']}>
                    {title && <h2 className="ud-heading-xl">{title}</h2>}
                    <ResponsiveBrowseCourseCarousel
                        courses={courses}
                        config={CAROUSEL_CONFIGURATIONS.CARDS_4_COLUMNS}
                    />
                </section>
            ))}
        </div>
    );
};
